import * as z from 'zod';

const quantityUnit = z.enum(['tCO2']);
const quantityType = z.enum(['Net', 'Gross']);
const emissionUnit = z.enum(['tCO2', 'tLNG', 'tMDO', 'MWh']);
const co2Source = z.enum(['Biogenic', 'Anthropogenic']);
const timestamp = z.iso.datetime().transform((d) => {
  const noMs = d.split('.')[0];

  if (!noMs) {
    return d;
  }

  return `${noMs}Z`;
}).pipe(z.iso.datetime());

const emission = z.object({
  description: z.string(),
  value: z.number(),
  unit: emissionUnit,
});

const location = z.object({
  description: z.string(),
  latitude: z.number(),
  longitude: z.number(),
});

const issuer = z.object({
  organization: z.string(),
  organizationId: z.string(),
  organizationUrl: z.string(),
  verifier: z.string(),
  verifierId: z.string(),
  verifierUrl: z.string(),
  storageCountry: z.string(),
});

const recipient = z.object({
  organization: z.string(),
  organizationId: z.string(),
  organizationUrl: z.string(),
  verifier: z.string(),
  verifierId: z.string(),
  verifierUrl: z.string(),
  captureCountry: z.string(),
  captureFacility: z.string(),
  registryId: z.string(),
  registryUrl: z.string(),
});

const metadata = z.object({
  storageCertificateId: z.string(),
  createdDate: timestamp,
  sealedDate: timestamp,
  expiryDate: timestamp,
  mrvVersion: z.string(),
  mrvUrl: z.string(),
  certificateUrl: z.string(),
});

const additionalFields = z.object({
  comment1: z.string().nullable(),
  comment2: z.string().nullable(),
  comment3: z.string().nullable(),
});

const capture = z.object({
  id: z.string(),
  quantityUnit,
  quantityType,
  quantityValue: z.number(),
  directEmissions: z.array(emission),
  associatedEmissions: z.array(emission),
  startTime: timestamp,
  endTime: timestamp,
  co2Source,
  co2Purity: z.number(),
  additionalFields,
});

const transportSegment = z.object({
  id: z.string(),
  transportMethod: z.string(), // NOTE(OLA): Enum ?
  responsibleParty: z.string(), // NOTE(OLA): Enum ?
  responsiblePartyName: z.string(),
  sequenceNumber: z.number(),
  quantityUnit,
  quantityType,
  quantityLoaded: z.number(),
  quantityUnloaded: z.number(),
  directEmissions: z.array(emission),
  associatedEmissions: z.array(emission),
  startTime: timestamp,
  endTime: timestamp,
  startLocation: location,
  endLocation: location,
  additionalFields,
});

const transport = z.object({
  id: z.string(),
  startTime: timestamp,
  endTime: timestamp,
  additionalFields,
  transportSegments: z.array(transportSegment),
});

const storageTerminal = z.object({
  id: z.string(),
  quantityUnit,
  quantityType,
  quantityReceived: z.number(),
  quantityInjected: z.number(),
  directEmissions: z.array(emission),
  associatedEmissions: z.array(emission),
  co2Purity: z.number(),
  startTime: timestamp,
  endTime: timestamp,
  location,
  additionalFields,
});

const storage = z.object({
  id: z.uuidv4(),
  quantityUnit,
  quantityType,
  quantityInjected: z.number(),
  quantityStored: z.number(),
  directEmissions: z.array(emission),
  associatedEmissions: z.array(emission),
  injectedTime: timestamp,
  storedTime: timestamp,
  location,
  additionalFields,
});

export const certificateSchema = z.object({
  id: z.string(),
  certificateVersion: z.string(),
  certificateIssuer: issuer,
  certificateRecipient: recipient,
  metadata,
  captures: z.array(capture),
  transport: transport.nullable(),
  storageTerminal,
  storage,
});

export type Certificate = z.infer<typeof certificateSchema>;
export type Emission = z.infer<typeof emission>;
export type AdditionalFields = z.infer<typeof additionalFields>;
